import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Mail, ArrowLeft } from "lucide-react";
import akLogo from "@/assets/ak-logo.png";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

const searchSchema = z.object({
  email: z.string().email().optional().catch(undefined),
});

export const Route = createFileRoute("/verify-email")({
  component: VerifyEmailPage,
  validateSearch: (search) => searchSchema.parse(search),
  head: () => ({
    meta: [
      { title: "Verify your email — AdKavi" },
      { name: "description", content: "Confirm your AdKavi email address to activate your account." },
      { property: "og:title", content: "Verify your email — AdKavi" },
      { property: "og:description", content: "Confirm your email to activate your AdKavi account." },
      { property: "og:url", content: "https://adkavi.ai/verify-email" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

const RESEND_COOLDOWN = 60;

function VerifyEmailPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { email: initialEmail } = Route.useSearch();
  const [email, setEmail] = useState(initialEmail ?? "");
  const [cooldown, setCooldown] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      if (session?.user?.email_confirmed_at) {
        toast.success(t("verify.toastVerified"));
        navigate({ to: "/dashboard" });
      }
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user?.email_confirmed_at) navigate({ to: "/dashboard" });
    });
    return () => subscription.unsubscribe();
  }, [navigate, t]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const tm = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(tm);
  }, [cooldown]);

  async function handleResend(e: React.FormEvent) {
    e.preventDefault();
    const parsed = z.string().trim().email(t("auth.zod.emailValid")).max(255).safeParse(email);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: parsed.data,
      options: { emailRedirectTo: `${window.location.origin}/dashboard` },
    });
    setLoading(false);
    if (error) {
      const msg = error.message.toLowerCase();
      if (msg.includes("rate") || msg.includes("seconds")) {
        toast.error(t("verify.toastWait"));
        setCooldown(RESEND_COOLDOWN);
        return;
      }
      toast.success(t("verify.toastMaybe"));
      setCooldown(RESEND_COOLDOWN);
      return;
    }
    toast.success(t("verify.toastSent"));
    setCooldown(RESEND_COOLDOWN);
  }

  return (
    <main className="min-h-screen flex items-center justify-center px-6 bg-background">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <img src={akLogo} alt="AdKavi logo" className="w-10 h-10 rounded-xl object-cover" />
          <span className="text-2xl font-bold">AdKavi</span>
        </Link>

        <div className="rounded-2xl border border-border/50 bg-card/50 backdrop-blur p-8">
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Mail className="w-7 h-7 text-primary" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-center mb-2">{t("verify.title")}</h1>
          <p className="text-sm text-muted-foreground text-center mb-6">
            {t("verify.descPrefix")}{" "}
            <span className="text-foreground font-medium">{email || t("verify.inbox")}</span>
            {t("verify.descSuffix")}
          </p>

          <form onSubmit={handleResend} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("verify.placeholder")}
              />
            </div>
            <Button
              type="submit"
              disabled={loading || cooldown > 0}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white"
            >
              {loading
                ? t("verify.sending")
                : cooldown > 0
                ? t("verify.resendIn").replace("{s}", String(cooldown))
                : t("verify.resend")}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/auth" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-4 h-4 mr-1" /> {t("verify.back")}
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
